import { QuotaFeatures } from "@/constants";
import {
  AddProductToSubscriptionRequest,
  OrganizationSubscription,
} from "@/types";

export type SubscriptionValidity = "month" | "year";

export interface EntitlementValues {
  videoSeconds: number;
  seats: number;
  workspaces: number;
  publications: number;
  concurrentGenerations: number;
}

export const CUSTOM_PLAN_PRODUCT_ID = "custom_plan_001";
export const CUSTOM_PLAN_PRODUCT_NAME = "Custom Plan";
export const CUSTOM_ADDON_PRODUCT_ID_PREFIX = "custom_addon_";
export const CUSTOM_ADDON_PRODUCT_NAME = "Custom Add-on";
const FREE_PLAN_PRODUCT_IDS = new Set(["free_account", "free_trial"]);

export function isFreeSubscription(
  subscription: OrganizationSubscription | null
): boolean {
  if (!subscription) return false;
  if (subscription.license_type === "Free") return true;
  const plan = subscription.subscribed_products?.find(
    (product) => product.product_type === "Plan"
  );
  return Boolean(plan && FREE_PLAN_PRODUCT_IDS.has(plan.product_id));
}

export function shouldCreateSubscription(
  subscription: OrganizationSubscription | null
): boolean {
  return !subscription || isFreeSubscription(subscription);
}

export function computeExpiry(
  validity: SubscriptionValidity,
  from: Date = new Date()
): string {
  const expiry = new Date(from);
  if (validity === "year") {
    expiry.setFullYear(expiry.getFullYear() + 1);
  } else {
    expiry.setMonth(expiry.getMonth() + 1);
  }
  return expiry.toISOString();
}

export function buildProductPayload({
  values,
  validity,
  subscription,
  now,
}: {
  values: EntitlementValues;
  validity: SubscriptionValidity;
  subscription: OrganizationSubscription | null;
  now?: Date;
}): AddProductToSubscriptionRequest {
  const isCreate = shouldCreateSubscription(subscription);
  const timestamp = (now ?? new Date()).getTime();

  return {
    product_id: isCreate
      ? CUSTOM_PLAN_PRODUCT_ID
      : `${CUSTOM_ADDON_PRODUCT_ID_PREFIX}${timestamp}`,
    product_name: isCreate
      ? CUSTOM_PLAN_PRODUCT_NAME
      : CUSTOM_ADDON_PRODUCT_NAME,
    product_type: isCreate ? "Plan" : "Addon",
    quantity: 1,
    expiry:
      isCreate || !subscription?.expiry
        ? computeExpiry(validity, now)
        : subscription.expiry,
    features: [
      { feature: QuotaFeatures.MediaProject, quota_limit: values.videoSeconds },
      {
        feature: QuotaFeatures.UserLicenseAssignment,
        quota_limit: values.seats,
      },
      { feature: QuotaFeatures.Workspace, quota_limit: values.workspaces },
      {
        feature: QuotaFeatures.ProjectPublication,
        quota_limit: values.publications,
      },
      {
        feature: QuotaFeatures.ConcurrentMediaGeneration,
        quota_limit: values.concurrentGenerations,
      },
    ],
  };
}
